<template>
  <div class="card border-0 shadow-sm mb-3">
    <div class="card-body p-3 p-lg-4">
      <div
        class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
      >
        <h6 class="text-uppercase text-muted mb-0 fw-semibold section-label">
          {{ $t("company_orders.transactions.registry_transactions") }}
        </h6>
        <span v-if="connectionLabel" class="small text-muted">
          {{ connectionLabel }}
        </span>
      </div>

      <p v-if="events.length === 0" class="text-muted mb-0">
        {{ $t("company_orders.transactions.not_yet_in_registry") }}
      </p>

      <div v-else class="table-responsive">
        <table class="table table-sm mb-0 align-middle">
          <thead>
            <tr>
              <th>{{ $t("company_orders.order_type") }}</th>
              <th>
                {{ $t("company_orders.transactions.registry_transaction") }}
              </th>
              <th>{{ $t("company_orders.transactions.occurred") }}</th>
              <th v-if="hasCounterparty">
                {{ $t("company_orders.transactions.counterparty") }}
              </th>
              <th class="text-end">MWh</th>
              <th class="text-end">
                {{ $t("company_orders.transactions.certificates") }}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="event in events" :key="event.id">
              <td>{{ formatEventType(event.event_type, t) }}</td>
              <td>
                <code>{{ event.registry_event_id }}</code>
              </td>
              <td>{{ formatDateTime(event.occurred_at) }}</td>
              <td v-if="hasCounterparty">
                {{ formatCounterparty(event) }}
              </td>
              <td class="text-end fw-semibold">
                {{ formatMwh(event.linked_volume) }}
              </td>
              <td class="text-end">{{ event.linked_certificate_count }}</td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="$emit('view:event', event)"
                >
                  {{ $t("company_orders.view") }}
                </button>
              </td>
            </tr>
            <tr v-if="events.length > 1" class="fw-semibold">
              <td :colspan="hasCounterparty ? 4 : 3">
                {{ $t("company_orders.transactions.total") }}
              </td>
              <td class="text-end">{{ formatMwh(totalVolume) }}</td>
              <td class="text-end">{{ totalCertificates }}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDateTime, formatMwh } from "@/composables/formatUtils";
import { sumBy } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { RegistryEventSummary } from "./transactionLogTypes";
import {
  formatConnection,
  formatEventType,
} from "./transactionTypePresentation";

/** The registry events connected to one company order, in ascending occurrence. */
const props = defineProps<{
  events: RegistryEventSummary[];
}>();

defineEmits<{
  "view:event": [event: RegistryEventSummary];
}>();

const { t } = useI18n();

const connectionLabel = computed(() => {
  const connection = props.events[0]?.connection;
  return connection ? formatConnection(connection, t) : "";
});

const hasCounterparty = computed(() =>
  props.events.some((event) => event.counterparty)
);

const totalVolume = computed(() => sumBy(props.events, "linked_volume"));

const totalCertificates = computed(() =>
  sumBy(props.events, "linked_certificate_count")
);

const formatCounterparty = (event: RegistryEventSummary): string =>
  [event.counterparty?.name, event.counterparty?.org_eecs]
    .filter(Boolean)
    .join(" · ");
</script>

<style scoped lang="scss">
@use "./order-details-shared";
</style>
